import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { Subscription } from 'rxjs';
import { FacilityObject } from 'src/app/core/services/facility.model';
import { FacilityService } from 'src/app/core/services/facility.service';
import { FeatureFlagsService } from 'src/app/core/shared-service/feature-flags.service';
import { environment } from '../../.././environments/environment';
import { environment as prodEnv } from '../../.././environments/environment.prod';
import { RootScopeDeclare } from '../../core/rootscope-declare';
import { RootScopeService } from '../../core/shared-service/root-scope.service';
import { CheckSsoEnabledService } from '../services/check-sso-enabled.service';
import { FacilityDetailsService } from '../services/facility-details.service';
import { ANNOUNCEMENTMODALS } from './announcement-modal.model';

@Component({
  selector: 'app-announcement-modal',
  templateUrl: './announcement-modal.component.html',
  styleUrls: ['./announcement-modal.component.scss'],
})
export class AnnouncementModalComponent implements OnInit, OnDestroy {
  currentFacility: FacilityObject;
  rootScopeData: RootScopeDeclare;
  currentModal: string;
  hasNewPasswordPolicy_LS: boolean;
  _modalMap: Map<string, any> = ANNOUNCEMENTMODALS;

  popups: string[] = [];
  dashboardPopups: string[] = [];

  @Output() onsignout: EventEmitter<string> = new EventEmitter();
  // subscriptions
  private sub = new Subscription();
  private sessionDuration = 60 * 60 * 1000; // 1 hour in milliseconds
  private alertBefore = 5 * 60 * 1000; // 5 minutes before expiry
  private timeToAlert = this.sessionDuration - this.alertBefore;
  private countdownTime = this.alertBefore; // in seconds
  private sessionTimeout: any;
  private intervalId: any;
  constructor(
    private rootScopeService: RootScopeService,
    private cookieService: CookieService,
    private ssoService: CheckSsoEnabledService,
    private facilityDetailService: FacilityDetailsService,
    private _facilityService: FacilityService,
    private _router: Router,
    private featureService: FeatureFlagsService
  ) {
    /** Below , For testing , QA and VTQA Env*/
    if (environment.fcConnectHostPath !== prodEnv.fcConnectHostPath) {
      this.sessionDuration = 1 * 60 * 1000; // 1 min in milliseconds
      this.alertBefore = 0.5 * 60 * 1000; // 30 seconds before expiry
      this.timeToAlert = this.sessionDuration - this.alertBefore;
      this.countdownTime = this.alertBefore;
    }
  }

  ngOnInit() {
    this.getRootScopeData();
    const fcHybridLegacHalo = this.featureService.isOn('facility-connect-hybrid-mode');
    if (fcHybridLegacHalo) {
      /*Implemented Hybrid Session Expiry logic inside onInit, Legacy can be accessed by impersonation itself.*/
      this.startSession();
    }

    // subscript facility change / when facility change
    this.currentFacility = this._facilityService.selectedFacility();
    this.sub.add(
      this._facilityService.selectedFacilitySource.subscribe((res) => {
        this.currentFacility = res;
        this.setUpAnnouncementModals();
      })
    );

    if (this.currentFacility['facilityId']) {
      this.setUpAnnouncementModals();
    }

    this.sub.add(
      this._router.events.subscribe((event) => {
        if (event instanceof NavigationEnd && event.url === '/dashboard') {
          // end of loading page
          this.setUpAnnouncementModals();
        } else if (event instanceof NavigationEnd && event.url === '/videoLibrary') {
          if (this.rootScopeData.managerData.hasCmsLegalReconsent === 'false') {
            this.popups.push('hasCmsLegalReconsent');
            this.displayAnnouncement();
          }
        }
      })
    );
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  getRootScopeData(): void {
    this.sub.add(
      this.rootScopeService.getRootScope().subscribe((rootScopeData) => {
        this.rootScopeData = rootScopeData;
      })
    );
  }

  setUpAnnouncementModals() {
    const popupData = this._facilityService.getAnnouncements();

    // not for this cookie
    const impersonatorCookie = this.cookieService.get('vtImpersonatorTokenKey');
    const _hideSurveyPopup = sessionStorage.getItem('hideSurveyPopup');
    const _hideOutdatedOnboardingPopup = sessionStorage.getItem('hideOutdatedOnboardingPopup');

    // only for dashboard
    if (this._router.url === '/dashboard') {
      // Survey popup
      if (
        popupData.showSurveyPopup &&
        (!_hideSurveyPopup || _hideSurveyPopup == 'false') &&
        (this.currentFacility.summary.countryIsoCode == 'US' ||
          this.rootScopeData.currentUser.preferredLanguage === 'en' ||
          this.rootScopeData.currentUser.preferredLanguage === 'en_US')
      ) {
        this.popups.push('survey');
      }

      // Navigation popup
      if (popupData.showNavigationPopup) {
        this.popups.push('navigation');
      }

      // LFOD popup
      if (
        popupData.showOnDemandClassesPopup &&
        this.currentFacility.summary.facilityRights.lfodEnabledEquipment == 'true' &&
        this.currentFacility.summary.userRights.lfodEnabledEquipment != 'H' &&
        this.featureService.isOn('se3HD')
      ) {
        this.popups.push('lfodEnabledEquipment');
      }

      if (popupData.showDigitalCoachPopup && this.currentFacility.summary.facilityRights.digitalCoach == 'true') {
        this.popups.push('digitalCoach');
      }

      if (
        popupData.showCustomLibraryPopup &&
        this.currentFacility.summary.facilityRights.customLibrary == 'true' &&
        this.currentFacility.summary.userRights.customLibrary != 'H' &&
        this.featureService.isOn('poseidon-milestone-3')
      ) {
        this.popups.push('customLibrary');
      }

      //  Halomedia popup
      if (popupData.showHalomediaPopup && this.currentFacility.summary.staffPermissionData.haloMediaOptedIn == 'true') {
        this.popups.push('haloModal');
      }

      // health and safety announcemnet
      if (popupData.showHealthSafetyPopup && this.currentFacility.summary.facilityRights.healthSafety == 'true') {
        this.popups.push('healthandsafety');
      }

      // equipmentavail widget announcemnet
      if (popupData.showMicrositePopup && this.currentFacility.summary.facilityRights.micrositeWidget == 'true') {
        this.popups.push('widgetModal');
      }

      // onboarding announcemnet modal
      if (
        popupData.showOnboardingPopup &&
        this.currentFacility.summary.mixedOnboardingFacility == 'true' &&
        this.currentFacility.summary.outdatedOnboardingAssetCount > 0 &&
        (!_hideOutdatedOnboardingPopup || _hideOutdatedOnboardingPopup == 'false')
      ) {
        this.popups.push('onboardingModal');
      }

      // multisite announcemnet
      const connectedFacilityorNot = this.currentFacility.summary.isConnected;
      if (popupData.showMultisitePopup && connectedFacilityorNot === 'true') {
        this.popups.push('multisiteModal');
      }

      if (popupData.fcAnnouncementBanner) {
        this.popups.push('fcAnnouncementBanner');
      }
    }

    // any page
    if (!impersonatorCookie == null || impersonatorCookie === '') {
      this.hasNewPasswordPolicy_LS = this.rootScopeData.currentUser.hasNewPasswordPolicy == 'true' ? true : false;
      if (this.rootScopeData.managerData.hasCmsLegalReconsent === 'false' && this._router.url === '/videoLibrary') {
        this.popups.push('hasCmsLegalReconsent');
      }
      if (
        this.rootScopeData.currentUser.resetPassword ||
        this.rootScopeData.managerData.isSubscriptionAccepted === false ||
        !this.rootScopeData.currentUser.acceptTnC
      ) {
        this.popups.push('pos');
      }

      if (
        !this.hasNewPasswordPolicy_LS ||
        !this.rootScopeData.currentUser.firstName ||
        !this.rootScopeData.currentUser.lastName
      ) {
        this.popups.push('mandatory');
      }

      if (this.rootScopeData.currentUser.isASBU) {
        this.linkSSO();
      }
    }

    this.displayAnnouncement();
  }

  displayAnnouncement() {
    this.dashboardPopups = [
      'survey',
      'haloModal',
      'healthandsafety',
      'widgetModal',
      'subscriptionModal',
      'onboardingModal',
      'multisiteModal',
      'digitalCoach',
      'videoLibrary',
      'lfodEnabledEquipment',
      'hasCmsLegalReconsent',
      'fcAnnouncementBanner',
    ];
    if (this.popups.length > 0) {
      if (this.dashboardPopups.indexOf(this.popups[this.popups.length - 1]) > -1) {
        if (this._router.url === '/dashboard') {
          this.currentModal = this.popups[this.popups.length - 1];
          this.rootScopeData.isAnnouncementModalOpen = true;
        } else if (this._router.url === '/videoLibrary') {
          this.currentModal = this.popups[this.popups.length - 1];
          this.rootScopeData.managerData.hasCmsLegalReconsent = true;
        } else {
          this.popups = [];
          this.rootScopeData.isAnnouncementModalOpen = false;
        }
      } else {
        this.currentModal = this.popups[this.popups.length - 1];
        this.rootScopeData.isAnnouncementModalOpen = true;
      }
    } else {
      this.rootScopeData.isAnnouncementModalOpen = false;
    }
  }

  closeAnnouncementModal(params?: any) {
    // ssologout | pos
    switch (this.currentModal) {
      case 'survey':
        if (!params) params = { showSurveyPopup: true, isSurveyTaken: false };
        this._facilityService.updatedFacilityPreferenceForUser(params);
        sessionStorage.setItem('hideSurveyPopup', 'true');
        break;
      case 'haloModal':
        this._facilityService.setAnnouncementsState('showHalomediaPopup', false);
        break;
      case 'digitalCoach':
        this._facilityService.setAnnouncementsState('showDigitalCoachPopup', false);
        break;
      case 'navigation':
        this._facilityService.setAnnouncementsState('showNavigationPopup', false);
        break;
      case 'healthandsafety':
        this._facilityService.setAnnouncementsState('showHealthSafetyPopup', false);
        break;
      case 'widgetModal':
        this._facilityService.setAnnouncementsState('showMicrositePopup', false);
        break;
      case 'subscriptionModal':
        this._facilityService.setAnnouncementsState('showSubscriptionPopup', false);
        break;
      case 'onboardingModal':
        this._facilityService.setAnnouncementsState('showOnboardingPopup', false);
        sessionStorage.setItem('hideOutdatedOnboardingPopup', 'true');
        break;
      case 'multisiteModal':
        this._facilityService.setAnnouncementsState('showMultisitePopup', false);
        break;
      case 'customLibrary':
        this._facilityService.setAnnouncementsState('showCustomLibraryPopup', false);
        break;
      case 'lfodEnabledEquipment':
        this._facilityService.setAnnouncementsState('showOnDemandClassesPopup', false);
        break;
      case 'hasCmsLegalReconsent':
        this._facilityService.setAnnouncementsState('hasCmsLegalReconsent', true);
        break;
      case 'fcAnnouncementBanner':
        this._facilityService.setAnnouncementsState('fcAnnouncementBanner', false);
        break;
      // case 'ssologout':
      // case 'pos':
      default:
        break;
    }

    // clear popups when user navigates to other pages
    if (params?.isNavigated) this.popups = [];

    this.dismissModal();
  }

  dismissModal() {
    if (this.currentModal === 'fcSessionExpiry') {
      this.onEndSession();
    } else {
      this.popups = this.popups.filter((p) => p !== this.currentModal);
      this.displayAnnouncement();
    }
  }

  closeOnboardingAnnouncemnetPopup() {
    if (this.popups.indexOf(this.currentModal) > -1) {
      this.popups.splice(this.popups.indexOf(this.currentModal), 1);
    }
    this.displayAnnouncement();
  }

  logoutSSO() {
    this.onsignout.emit();
  }

  linkSSO() {
    if (!this.rootScopeData.currentUser.isSSOFederated && this.rootScopeData.currentUser.managerType != 'N') {
      this.sub.add(
        this.ssoService.linkSSOUser().subscribe(
          (data) => {
            this.popups.push('ssologout');
            this.displayAnnouncement();
          },
          (error) => {
            console.error('API failed.');
          }
        )
      );
    }
  }

  // calling this function since the popup is closing on click of outer div
  stopEventCapturing(e: Event): void {
    e.stopPropagation();
  }

  public onExtendSession() {
    this.popups = this.popups.filter((p) => p !== 'fcSessionExpiry');
    clearTimeout(this.sessionTimeout);
    this.resetCountdown();
    this.timeToAlert = this.countdownTime + this.sessionDuration - this.alertBefore;
    this.countdownTime = this.alertBefore;
    this.startSession(); // restart the session
  }

  public onEndSession() {
    this.popups = this.popups.filter((p) => p !== 'fcSessionExpiry');
    clearTimeout(this.sessionTimeout);
  }

  private startSession() {
    this.scheduleSessionTimeout();
  }

  private scheduleSessionTimeout() {
    this.sessionTimeout = setTimeout(() => {
      this.showFcSessionExpiry();
      this.startCountdown();
    }, this.timeToAlert);
  }

  private showFcSessionExpiry() {
    this.popups.push('fcSessionExpiry');
    this.currentModal = 'fcSessionExpiry';
  }

  private startCountdown() {
    this.resetCountdown();

    this.intervalId = setInterval(() => {
      this.countdownTime += -1000;
      if (this.countdownTime <= 0) {
        this.handleSessionTimeUp();
      }
    }, 1000); // Update every second
  }

  private handleSessionTimeUp() {
    clearInterval(this.intervalId);
    window.location.replace(environment.fcConnectHostPath); // Redirect to external link
  }

  private resetCountdown() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
}
